interface RestorableNode {
  level?: number
  rowIndex?: number | null
  data?: unknown
  parent?: RestorableNode | null
}

interface GetRowToRememberParams {
  node?: RestorableNode | null
  /** The same callback that feeds getServerSideGroupKey, so both sides produce identical keys. */
  getRowKey?: (data: unknown) => string
}

interface RememberedRow {
  rowIndex: number | null
  /** Set only when a child row was clicked, naming the parent that has to be opened again. */
  expandedRowKey: string | null
}

/**
 * What the list has to remember when a row is activated, so that returning to it restores the view.
 *
 * A click on a child row is recorded as its parent: the child only exists while the parent is open,
 * so the parent is both the row to scroll to and the row to open again.
 *
 * Extracted from the list component to stay unit testable — the grid itself cannot be mounted in
 * jest, because tree data needs the RowGroupingModule that the CJS entry point does not export.
 */
export const getRowToRemember = ({ node, getRowKey }: GetRowToRememberParams): RememberedRow | null => {
  if (!node) {
    return null
  }

  const isChildRow = Number(node.level) > 0

  if (!isChildRow) {
    return { rowIndex: node.rowIndex ?? null, expandedRowKey: null }
  }

  const parent = node.parent

  // without the parent's data there is no key to open it by, and its row index is the wrong one
  if (!parent?.data || !getRowKey) {
    return null
  }

  return {
    rowIndex: parent.rowIndex ?? null,
    expandedRowKey: getRowKey(parent.data) || null,
  }
}

/**
 * Whether a row is the one that was remembered. Answers two questions with one comparison: whether
 * ag-grid has to start the row out open, and whether an expand event completes the restore.
 */
export const shouldOpenByDefault = (pendingKey: string | null | undefined, rowKey: string | null | undefined): boolean =>
  Boolean(pendingKey) && pendingKey === rowKey
